import { CancelledError, QueryClient } from '@tanstack/react-query';
import { redirect, type MiddlewareFunction } from 'react-router';

import type { SessionPlQueryReturnData } from '~/services/ClientServiceTS';
import { getAccountDetailsFromURL } from '~/utils/utils';

import { sessionContext } from './sessionContext';
import {
  getSessionPlQueryOptions,
  SESSION_PL_MIDDLEWARE_STALE_TIME_MS,
} from './sessionPlQueryOptions';

function getNoSessionRedirect(request: Request) {
  const url = new URL(request.url);
  const noSessionReferer =
    url.pathname !== '/no-session'
      ? new URLSearchParams({ referer: url.pathname + url.search }).toString()
      : '';
  return redirect(`/no-session?${noSessionReferer}`);
}

function getPreviousSession(queryClient: QueryClient) {
  const entries = queryClient.getQueriesData<SessionPlQueryReturnData>({
    queryKey: ['session.pl'],
  });
  return entries.find(([, data]) => data)?.[1];
}

function isAccountSwitch(
  previous: SessionPlQueryReturnData | undefined,
  accountId?: string,
  accountType?: string,
) {
  if (!previous || !accountId) return false;
  const isPartner = accountType === 'partner';
  return previous.id.toString() !== accountId || previous.isPartner !== isPartner;
}

/**
 * Account-scoped session middleware: resolves session.pl (network when stale),
 * syncs React Query, and exposes session via `sessionContext`.
 *
 * Short `SESSION_PL_MIDDLEWARE_STALE_TIME_MS` covers account-root → permission
 * redirect (two navigations) without a second network fetch.
 *
 * Does not clear the query cache on account switch — account-scoped keys /
 * `client_id` on requests keep cache identity correct. SessionHandler gates
 * the outlet until session matches the URL.
 */
export function resolveSessionMiddleware(queryClient: QueryClient): MiddlewareFunction {
  return async ({ request, context }, next) => {
    const url = new URL(request.url);
    const { accountType, accountId } = getAccountDetailsFromURL(url.pathname) ?? {};

    try {
      if (isAccountSwitch(getPreviousSession(queryClient), accountId, accountType)) {
        await queryClient.cancelQueries({ queryKey: ['session.pl'] });
      }

      const sessionData = await queryClient.fetchQuery({
        ...getSessionPlQueryOptions({ accountId, accountType }),
        staleTime: SESSION_PL_MIDDLEWARE_STALE_TIME_MS,
      });
      context.set(sessionContext, sessionData);
    } catch (e) {
      if (e instanceof Response) {
        throw e;
      }
      console.error(e);
      if (e instanceof CancelledError) {
        throw e;
      }
      throw getNoSessionRedirect(request);
    }

    return next();
  };
}
